import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import { useGetAllInventoryProducts } from "../../utils/react-query/queryInventory/queries";
import { useGetAvailableStock } from "../../utils/react-query/queryReservations/queries";
import CustomButton from "../../components/Button/CustomButton";
import type {
  CreateReservationPayload,
  EditReservationPayload,
  ReservationDetail,
} from "./types";
import {
  buildAvailableStockQuery,
  indexAvailableStock,
  mapInsufficientStockError,
  validateQuantities,
  type StockErrorMap,
} from "../../utils/stockAvailability";
import { ProductAutocomplete } from "../../components/ProductAutocomplete/ProductAutocomplete";
import styles from "./ReservationModal.module.scss";

interface ReservationModalProps {
  open: boolean;
  /** If provided, the modal is in edit mode */
  reservation?: ReservationDetail | null;
  onClose: () => void;
  onCreate: (payload: CreateReservationPayload) => Promise<void>;
  onEdit: (id: number, payload: EditReservationPayload) => Promise<void>;
}

interface ItemRow {
  product_id: number;
  storage_address_id: number;
  quantity: number;
}

const emptyItem: ItemRow = { product_id: 0, storage_address_id: 0, quantity: 0 };

export function ReservationModal({
  open,
  reservation,
  onClose,
  onCreate,
  onEdit,
}: ReservationModalProps) {
  const isEditMode = !!reservation;
  // The complete catalog, not one inventory page: GET /v2/inventory is
  // paginated by id ASC, so newly created products live on the last pages.
  const { data: inventory, isLoading: isLoadingInventory } =
    useGetAllInventoryProducts();

  const [notes, setNotes] = useState(reservation?.notes ?? "");
  const [quoteNumber, setQuoteNumber] = useState("");
  const [quoteFieldError, setQuoteFieldError] = useState<string | null>(null);
  const [clientName, setClientName] = useState("");
  const [items, setItems] = useState<ItemRow[]>(
    reservation?.items?.map((i) => ({
      product_id: i.product_id,
      storage_address_id: i.storage_address_id,
      quantity: i.quantity,
    })) ?? [{ ...emptyItem }]
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Per-row stock validation messages, keyed by item index.
  const [stockErrors, setStockErrors] = useState<StockErrorMap>({});

  // Debounce the available-stock query 300ms after the items change so the
  // user can finish selecting product + address before we hit the endpoint.
  const [debouncedStockQuery, setDebouncedStockQuery] = useState<string | null>(
    null
  );
  useEffect(() => {
    const query = buildAvailableStockQuery(items);
    const timer = setTimeout(() => setDebouncedStockQuery(query), 300);
    return () => clearTimeout(timer);
  }, [items]);

  // Fetch available stock only when at least one row is fully selected.
  const { data: stockData, isFetching: isFetchingStock } =
    useGetAvailableStock(debouncedStockQuery);

  // Lookup of "product_id:storage_address_id" -> available_stock.
  const availableMap = useMemo(
    () => indexAvailableStock(stockData?.items ?? []),
    [stockData]
  );

  // Re-run client-side validation whenever the form items or the stock data
  // change. Backend 422 mapping (handled in handleConfirm's catch block) is
  // additive and not clobbered here because this effect depends only on
  // stockData + items, neither of which changes during a failed submit.
  useEffect(() => {
    setStockErrors(validateQuantities(items, availableMap));
  }, [items, availableMap]);

  // Reset form when reservation changes
  const handleEntering = () => {
    setNotes(reservation?.notes ?? "");
    setQuoteNumber("");
    setQuoteFieldError(null);
    setClientName("");
    setStockErrors({});
    setItems(
      reservation?.items?.map((i) => ({
        product_id: i.product_id,
        storage_address_id: i.storage_address_id,
        quantity: i.quantity,
      })) ?? [{ ...emptyItem }]
    );
  };

  // Validation
  const isItemsValid =
    items.length > 0 &&
    items.every(
      (item) =>
        item.product_id > 0 &&
        item.storage_address_id > 0 &&
        Number.isInteger(item.quantity) &&
        item.quantity > 0
    );
  const isQuoteValid =
    isEditMode ||
    (quoteNumber.trim().length > 0 && quoteNumber.trim().length <= 64);
  const hasStockErrors = Object.keys(stockErrors).length > 0;
  // Client-side stock validation is primary: any per-row stock error blocks
  // Save so the user must bring every quantity within available stock before
  // the backend ever sees the create/edit request.
  const canConfirm =
    isItemsValid && isQuoteValid && !isLoadingInventory && !hasStockErrors;

  const handleItemChange = (
    index: number,
    field: keyof ItemRow,
    value: number
  ) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  const addItem = () => {
    setItems([...items, { ...emptyItem }]);
  };

  const removeItem = (index: number) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setIsSubmitting(true);
    setQuoteFieldError(null);
    try {
      if (isEditMode && reservation) {
        // Edit mode: send items as add/update/remove
        const payload: EditReservationPayload = {
          notes: notes.trim() || undefined,
          items: {
            add: items
              .filter((item) => !reservation.items?.some((ri) => ri.product_id === item.product_id && ri.storage_address_id === item.storage_address_id))
              .map((item) => ({
                product_id: item.product_id,
                storage_address_id: item.storage_address_id,
                quantity: item.quantity,
              })),
            update: items
              .filter((item) => reservation.items?.some((ri) => ri.product_id === item.product_id && ri.storage_address_id === item.storage_address_id))
              .map((item) => {
                const existing = reservation.items!.find(
                  (ri) =>
                    ri.product_id === item.product_id &&
                    ri.storage_address_id === item.storage_address_id
                )!;
                return { id: existing.id, quantity: item.quantity };
              }),
          },
        };
        await onEdit(reservation.id, payload);
      } else {
        // Create mode
        const payload: CreateReservationPayload = {
          quote_number: quoteNumber.trim(),
          client_name: clientName.trim() || undefined,
          notes: notes.trim() || undefined,
          items: items.map((item) => ({
            product_id: item.product_id,
            storage_address_id: item.storage_address_id,
            quantity: item.quantity,
          })),
        };
        await onCreate(payload);
      }
    } catch (err: any) {
      // Backend 422 safety net: if the server still reports insufficient stock
      // (race condition, concurrent reservations, etc.), surface it inline per
      // row exactly like the client-side validation would.
      const backendStockErrors = mapInsufficientStockError(err, items);
      if (Object.keys(backendStockErrors).length > 0) {
        setStockErrors((prev) => ({ ...prev, ...backendStockErrors }));
      }
      // Pluck backend field error for quote_number (inline display)
      // Non-field errors: parent (handleCreate) already shows generic errorToast
      const fieldErrors = err?.response?.data?.errors?.quote_number;
      if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
        setQuoteFieldError(fieldErrors[0]);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{ onEntering: handleEntering }}
    >
      <DialogTitle>
        {isEditMode ? "Editar Reserva" : "Nueva Reserva"}
      </DialogTitle>

      <DialogContent>
        {isEditMode ? (
          <Typography variant="body2" style={{ marginBottom: 8 }}>
            {reservation?.quote_number || "Sin cotización"}
          </Typography>
        ) : (
          <>
            <TextField
              label="Cotización"
              variant="outlined"
              fullWidth
              margin="normal"
              value={quoteNumber}
              onChange={(e) => {
                setQuoteNumber(e.target.value);
                setQuoteFieldError(null);
              }}
              placeholder="COT-YYYYMMDD-HHMMSS"
              inputProps={{ maxLength: 64, "aria-label": "cotizacion" }}
              error={!!quoteFieldError}
              helperText={quoteFieldError}
            />

            <TextField
              label="Nombre del cliente"
              variant="outlined"
              fullWidth
              margin="normal"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Ingrese el nombre del cliente"
              inputProps={{ maxLength: 200, "aria-label": "client-name" }}
            />
          </>
        )}

        <TextField
          label="Notas"
          variant="outlined"
          fullWidth
          multiline
            minRows={2}
          margin="normal"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          inputProps={{ maxLength: 500, "aria-label": "notas" }}
        />

        <Typography variant="subtitle2" className={styles.subtitle}>
          Items
        </Typography>

        {items.map((item, index) => (
          <div
            key={index}
            className={styles.itemRow}
          >
            <ProductAutocomplete
              products={inventory?.products ?? []}
              value={item.product_id}
              onChange={(productId) =>
                handleItemChange(index, "product_id", productId)
              }
              inputAriaLabel={`product-id-${index}`}
              disabled={isLoadingInventory}
              className={styles.itemField}
            />
            <TextField
              select
              label="Bodega"
              variant="outlined"
              size="small"
              value={item.storage_address_id || ""}
              onChange={(e) =>
                handleItemChange(
                  index,
                  "storage_address_id",
                  parseInt(e.target.value, 10) || 0
                )
              }
              inputProps={{ "aria-label": `address-id-${index}` }}
              className={styles.itemField}
              disabled={isLoadingInventory}
            >
              <MenuItem value={0}>Seleccionar bodega</MenuItem>
              {inventory?.storageAddresses.map((address) => (
                <MenuItem key={address.id} value={address.id}>
                  {address.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Cantidad"
              type="number"
              variant="outlined"
              size="small"
              value={item.quantity || ""}
              onChange={(e) =>
                handleItemChange(index, "quantity", parseInt(e.target.value, 10) || 0)
              }
              inputProps={{ min: 1, "aria-label": `quantity-${index}` }}
              className={styles.itemField}
            />
            <IconButton
              aria-label="Eliminar item"
              onClick={() => removeItem(index)}
              disabled={items.length <= 1}
              size="small"
            >
              <DeleteIcon />
            </IconButton>
            <div className={styles.stockInfo}>
              {stockErrors[index] ? (
                <Typography
                  variant="caption"
                  color="error"
                  role="alert"
                  aria-label={`stock-error-${index}`}
                >
                  {stockErrors[index]}
                </Typography>
              ) : (
                item.product_id > 0 &&
                item.storage_address_id > 0 &&
                availableMap.has(
                  `${item.product_id}:${item.storage_address_id}`
                ) && (
                  <Typography
                    variant="caption"
                    aria-label={`stock-available-${index}`}
                  >
                    Stock disponible:{" "}
                    {
                      availableMap.get(
                        `${item.product_id}:${item.storage_address_id}`
                      )
                    }
                  </Typography>
                )
              )}
              {isFetchingStock && !stockErrors[index] && (
                <Typography variant="caption">Consultando stock…</Typography>
              )}
            </div>
          </div>
        ))}

        <CustomButton
          color="outlined"
          icon={AddIcon}
          onClick={addItem}
          size="small"
          className={styles.addButton}
        >
          Agregar item
        </CustomButton>
      </DialogContent>

      <DialogActions>
        <CustomButton
          color="outlined"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={handleConfirm}
          color="primary"
          disabled={!canConfirm || isSubmitting}
          isLoading={isSubmitting}
        >
          {isEditMode ? "Guardar" : "Crear"}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
}

export default ReservationModal;
